import { Button, Card, Text, TextInput, VStack } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { AppShell } from "@/components/app-shell";
import {
  operatorNameError,
  updateCurrentOperatorName,
  validateOperatorName,
} from "@/lib/operator-profile";
import { useSession } from "@/services/auth0/session";

const styles = stylex.create({
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 12,
    fontSize: "0.875rem",
    color: "var(--color-text-secondary)",
    textDecoration: "none",
  },
  card: { width: "100%", maxWidth: "32rem" },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },
  error: { color: "var(--color-error)" },
  success: { color: "var(--color-success)" },
});

export function ProfileScreen() {
  const { operator, token } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(operator?.carrierUser?.name ?? "");
  const [message, setMessage] = useState<{
    type: "error" | "success";
    text: string;
  } | null>(null);

  const updateName = useMutation({
    mutationFn: () => updateCurrentOperatorName(name),
    onSuccess: (updatedOperator) => {
      queryClient.setQueryData(["operator", "me", token], updatedOperator);
      void queryClient.invalidateQueries({ queryKey: ["operator", "me"] });
      setName(updatedOperator.carrierUser?.name ?? name.trim());
      setMessage({ type: "success", text: "Name updated" });
    },
    onError: (error) =>
      setMessage({ type: "error", text: operatorNameError(error) }),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateOperatorName(name);
    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }
    setMessage(null);
    updateName.mutate();
  };

  return (
    <AppShell>
      <Link to="/" {...stylex.props(styles.backLink)}>
        <ArrowLeft size={16} /> My trips
      </Link>
      <div {...stylex.props(styles.card)}>
        <Card padding={5}>
          <form onSubmit={submit} {...stylex.props(styles.form)}>
            <VStack gap={1} align="start">
              <Text as="h1" size="lg" weight="semibold">
                Operator profile
              </Text>
              <Text size="sm" color="secondary">
                Dispatch and carrier teams use this name to identify you.
              </Text>
            </VStack>
            <TextInput
              label="Name"
              isRequired
              value={name}
              onChange={(value) => {
                setName(value);
                if (message?.type === "error") setMessage(null);
              }}
              placeholder="Enter your full name"
            />
            <Text size="sm" color="secondary">
              Signed in as {operator?.carrierUser?.email ?? "operator"}
            </Text>
            {message ? (
              <span
                role={message.type === "error" ? "alert" : "status"}
                {...stylex.props(
                  message.type === "error" ? styles.error : styles.success
                )}
              >
                {message.text}
              </span>
            ) : null}
            <div {...stylex.props(styles.actions)}>
              <Button
                type="submit"
                variant="primary"
                label="Save name"
                isLoading={updateName.isPending}
                isDisabled={updateName.isPending}
              />
            </div>
          </form>
        </Card>
      </div>
    </AppShell>
  );
}
